import { Injectable, inject } from '@angular/core';
import { Observable, of as observableOf } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { ServiceCategoryModel, ServiceMasterCategoryModel, ServiceModel } from './model';
import { ServiceCatalogState } from './state';
import * as selectors from './selectors';
import * as actions from './actions';
import { UserFacade } from '../../user/facade';

@Injectable()
export class ServiceCatalogFacade {
  private store = inject<Store<ServiceCatalogState>>(Store);
  private userFacade = inject(UserFacade);

  public entities$ = this.store.pipe(select(selectors.selectAll));
  public filtered$ = this.store.pipe(select(selectors.selectFiltered));

  constructor() {
    this.getAll();
    this.filter();
  }

  public getAll() {
    this.userFacade.selected().subscribe(user => {
      if (user) {
        this.store.dispatch(new actions.GetAllRequestAction());
      }
    });
  }

  public enable(codes: number[]) {
    this.store.dispatch(new actions.EnableRequestAction(codes));
  }

  public disable(codes: number[]) {
    this.store.dispatch(new actions.DisableRequestAction(codes));
  }

  public sync() {
    this.store.dispatch(new actions.SyncRequestAction());
  }

  public filter() {
    this.userFacade.selected().subscribe(user => {
      if (user) {
        this.store.dispatch(new actions.FilterRequestAction());
      }
    });
  }

  public updateService(service: ServiceModel) {
    this.store.dispatch(new actions.UpdateServiceRequestAction(service));
  }

  public updateCategory(category: ServiceCategoryModel) {
    this.store.dispatch(new actions.UpdateCategoryRequestAction(category));
  }

  public uploadCategoryIcon(category: ServiceCategoryModel, file: File) {
    this.store.dispatch(new actions.UploadCategoryIconRequestAction(category, file));
  }

  public deleteCategoryIcon(category: ServiceCategoryModel) {
    this.store.dispatch(
      new actions.DeleteCategoryIconRequestAction(<ServiceCategoryModel>{
        id: category.id,
        code: category.code,
        nameUa: category.nameUa,
        nameRu: category.nameRu,
      })
    );
  }

  public uploadMasterCategoryIcon(masterCategory: ServiceMasterCategoryModel, file: File) {
    this.store.dispatch(new actions.UploadMasterCategoryIconRequestAction(masterCategory, file));
  }

  public deleteMasterCategoryIcon(masterCategory: ServiceMasterCategoryModel) {
    this.store.dispatch(new actions.DeleteMasterCategoryIconRequestAction(masterCategory));
  }

  public upPriorityCategory(category: ServiceCategoryModel) {
    this.store.dispatch(new actions.UpPriorityCategoryRequestAction(category));
  }

  public downPriorityCategory(category: ServiceCategoryModel) {
    this.store.dispatch(new actions.DownPriorityCategoryRequestAction(category));
  }

  public upPriorityMasterCategory(masterCategory: ServiceMasterCategoryModel) {
    this.store.dispatch(new actions.UpPriorityMasterCategoryRequestAction(masterCategory));
  }

  public downPriorityMasterCategory(masterCategory: ServiceMasterCategoryModel) {
    this.store.dispatch(new actions.DownPriorityMasterCategoryRequestAction(masterCategory));
  }

  public getError(): Observable<any> {
    return this.store.pipe(select(selectors.selectError));
  }

  public isLoading(): Observable<boolean> {
    return this.store.pipe(select(selectors.selectIsLoading));
  }
}
